/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	ActionIcon,
	Avatar,
	Box,
	Flex,
	Group,
	Image,
	Menu,
	Popover,
	Stack,
} from '@mantine/core';
import {
	IconChevronDown,
	IconInfoSquareRounded,
	IconLogout,
	IconSettings,
	IconUserCircle,
	IconX,
} from '@tabler/icons-react';
import React, { memo, useState } from 'react';

import { useUpdateProfile } from '@features/account-settings/api/update-profile';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useDisclosure } from '@mantine/hooks';
import { useStore } from '@store/index';
import capitalize from 'lodash/capitalize';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Text } from '../../Text';
import { Title } from '../../Title';
import classes from './UserProfileMenu.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type TUserProfileMenuProps = {
	userName: string;
	userRole: string;
	avatarSrc?: string;
	onLogout?: () => void;
};

/* -------------------------------------------------------------------------- */
/*                              UserProfileMenu                              */
/* -------------------------------------------------------------------------- */

export const UserProfileMenu: React.FC<TUserProfileMenuProps> =
	memo(({ userName, userRole, avatarSrc, onLogout }) => {
		/* ******************************** HOOKS ******************************* */

		const [opened, setOpened] = useState(false);
		const { t, i18n } = useTranslation();

		let navigate = useNavigate();

		const [openedLangugageDropdown, { close, toggle }] =
			useDisclosure(false);

		const [lng, setLng] = useLocalStorage('lng', 'fr');

		const updateProfileMutation = useUpdateProfile({
			mutationConfig: {},
		});

		const { getUser } = useStore();

		const user = getUser();

		/* ******************************** RENDERING ******************************* */
		return (
			<Flex align="center" gap="12px">
				<Menu
					opened={opened}
					classNames={classes}
					width={300}
					position="bottom-end"
					onClose={() => {
						close();
						setOpened(false);
					}}
				>
					<Menu.Target>
						<Avatar
							style={{ cursor: 'pointer' }}
							variant="outline"
							size={48}
							radius="xl"
							color="var(--mantine-color-igluBorder-9)"
							onClick={() => setOpened(true)}
							data-testid="user-menu-button"
							aria-haspopup="true"
							aria-expanded={opened}
							aria-label="User menu"
							src={avatarSrc}
						>
							<IconUserCircle
								color="var(--mantine-color-igluPrimary-9)"
								stroke={1.5}
								size={24}
							/>
						</Avatar>
					</Menu.Target>
					<Menu.Dropdown>
						<Group
							w="100%"
							justify="space-between"
							pb={'var(--mantine-spacing-md)'}
						>
							<Title
								c="var(--mantine-color-igluPrimary-9)"
								order={6}
								data-testid="profile-title"
							>
								{t('global.profile')}
							</Title>
							<ActionIcon
								data-testid="close-menu-button"
								variant="outline"
								size="xl"
								radius="xl"
								color="var(--mantine-color-igluBorder-9)"
								onClick={() => setOpened(false)}
								aria-label="Close user menu"
							>
								<IconX
									color="var(--mantine-color-igluTextSecondary-9)"
									size={20}
									stroke={1.5}
								/>
							</ActionIcon>
						</Group>
						<Stack gap={'var(--mantine-spacing-xs)'}>
							<Popover
								opened={openedLangugageDropdown}
								position="bottom"
								withArrow
								shadow="md"
								offset={0}
								radius={'var(--mantine-radius-md)'}
								onDismiss={close}
							>
								<Popover.Target>
									<Menu.Item
										onClick={toggle}
										leftSection={
											<Image
												radius={
													'var(--mantine-radius-md)'
												}
												h={24}
												w={24}
												src={
													lng === 'fr'
														? 'https://flagcdn.com/w40/fr.png'
														: 'https://flagcdn.com/w40/gb.png'
												}
											/>
										}
										rightSection={
											<IconChevronDown
												color="var(--mantine-color-igluTextSecondary-9)"
												size={24}
											/>
										}
										closeMenuOnClick={false}
									>
										{lng === 'fr'
											? 'Français'
											: 'English'}
									</Menu.Item>
								</Popover.Target>
								<Popover.Dropdown
									style={{
										minWidth: '220px',
										padding: '15px 10px',
									}}
								>
									<Stack gap={'10px'}>
										<Box
											onClick={(event) => {
												event.stopPropagation();
												setLng('fr');
												i18n.changeLanguage('fr');
												if (
													typeof user != 'boolean' &&
													user?.id
												)
													updateProfileMutation.mutate({
														id: user.id,
														locale: {
															language: 'fr',
														},
													});
											}}
											className={classes.customItem}
										>
											<Image
												className={classes.logoCountry}
												src={`https://flagcdn.com/w40/fr.png`}
											/>
											<span>{'Français'}</span>
										</Box>
										<Box
											onClick={(event) => {
												event.stopPropagation();
												setLng('en');
												i18n.changeLanguage('en');
												if (
													typeof user != 'boolean' &&
													user?.id
												)
													updateProfileMutation.mutate({
														id: user.id,
														locale: {
															language: 'en',
														},
													});
											}}
											className={classes.customItem}
										>
											<Image
												className={classes.logoCountry}
												src={`https://flagcdn.com/w40/gb.png`}
											/>
											<span>{'English'}</span>
										</Box>
									</Stack>
								</Popover.Dropdown>
							</Popover>
							<Menu.Item
								onClick={() => {
									navigate('/account-settings');
								}}
								closeMenuOnClick={true}
								leftSection={
									<IconSettings
										color="var(--mantine-color-igluTextSecondary-9)"
										size={24}
									/>
								}
							>
								{t('sidebar.settings')}
							</Menu.Item>

							<Menu.Item
								closeMenuOnClick={true}
								leftSection={
									<IconInfoSquareRounded
										color="var(--mantine-color-igluTextSecondary-9)"
										size={24}
									/>
								}
								disabled
							>
								{t('sidebar.support')}
							</Menu.Item>

							<Menu.Item
								closeMenuOnClick={true}
								leftSection={
									<IconLogout
										color="var(--mantine-color-igluTextSecondary-9)"
										size={24}
									/>
								}
								onClick={onLogout}
							>
								{t('sidebar.logout')}
							</Menu.Item>
						</Stack>
					</Menu.Dropdown>
				</Menu>

				<Stack visibleFrom="sm" gap="0px">
					<Text
						c="var(--mantine-color-igluTextPrimary-9)"
						variant="subtitle1"
						data-testid="user-welcome-text"
						tt="capitalize"
					>
						{`${t('global.welcome')}, ${capitalize(userName)}`}
					</Text>
					<Text
						c="var(--mantine-color-igluTextSecondary-9)"
						variant="caption"
						data-testid="user-role-text"
					>
						{capitalize(userRole)}
					</Text>
				</Stack>
			</Flex>
		);
	});
